import { sql } from "drizzle-orm";
import { pgTable, varchar, text, integer, jsonb, timestamp, numeric, boolean, unique, uniqueIndex, index, foreignKey, check } from "drizzle-orm/pg-core";
import { brands } from "./schema";
import { users } from "./models/auth";
import type { VideoWorkflow, VideoStage, VideoJson, VideoApprovalSnapshot, VideoSlotType, VideoAssetType, VideoAssetMetadata, VideoDispatchState, VideoAdmissionState, VideoContinuation } from "./video-contracts";

const id = () => varchar("id").primaryKey().default(sql`gen_random_uuid()`);
const brandId = () => varchar("brand_id").notNull().references(() => brands.id);
const createdAt = () => timestamp("created_at").notNull().defaultNow();

export const videoBlueprints = pgTable("video_blueprints", {
  id: id(), brandId: brandId(), name: text("name").notNull(), conceptFamily: text("concept_family"),
  status: text("status").$type<"draft" | "in_review" | "approved" | "archived">().notNull().default("draft"),
  sourceReferenceId: text("source_reference_id"),
  approvedVersionId: varchar("approved_version_id"),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  createdAt: createdAt(), updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [unique("video_blueprints_brand_id_identity").on(t.brandId, t.id),
  check("video_blueprints_status_check", sql`${t.status} IN ('draft','in_review','approved','archived')`)]);

export const videoBlueprintVersions = pgTable("video_blueprint_versions", {
  id: id(), brandId: brandId(), blueprintId: varchar("blueprint_id").notNull(),
  version: integer("version").notNull(), creativeSpec: jsonb("creative_spec_json").$type<VideoJson>().notNull(),
  snapshot: jsonb("snapshot_json").$type<VideoApprovalSnapshot>().notNull(),
  projectId: text("project_id").notNull(), projectVersion: integer("project_version").notNull(),
  projectUri: text("tesseract_project_uri").notNull(), previewUri: text("preview_uri").notNull(),
  approvedAt: timestamp("approved_at"), approvedBy: varchar("approved_by").references(() => users.id),
  changeSummary: text("change_summary").notNull(),
  createdByActor: text("created_by_actor").$type<"user" | "astra" | "system">().notNull(),
  createdAt: createdAt(),
}, t => [
  unique("video_versions_brand_identity").on(t.brandId, t.id),
  unique("video_versions_blueprint_identity").on(t.brandId, t.blueprintId, t.id),
  unique("video_versions_number_unique").on(t.brandId, t.blueprintId, t.version),
  foreignKey({ columns: [t.brandId, t.blueprintId], foreignColumns: [videoBlueprints.brandId, videoBlueprints.id] }),
  check("video_versions_positive", sql`${t.version} > 0 AND ${t.projectVersion} > 0`),
  check("video_versions_approval_pair", sql`(${t.approvedAt} IS NULL) = (${t.approvedBy} IS NULL)`),
]);

export const videoBlueprintSlots = pgTable("video_blueprint_slots", {
  id: id(), brandId: brandId(), blueprintId: varchar("blueprint_id").notNull(), versionId: varchar("version_id").notNull(),
  slotKey: text("slot_key").notNull(), type: text("type").$type<VideoSlotType>().notNull(),
  required: boolean("required").notNull(), locked: boolean("locked").notNull(),
  constraints: jsonb("constraints_json").$type<VideoJson>().notNull(), allowedVariation: jsonb("allowed_variation_json").$type<VideoJson>().notNull(),
  createdAt: createdAt(),
}, t => [unique("video_slots_version_key_unique").on(t.brandId, t.versionId, t.slotKey),
  foreignKey({ columns: [t.brandId, t.blueprintId, t.versionId], foreignColumns: [videoBlueprintVersions.brandId, videoBlueprintVersions.blueprintId, videoBlueprintVersions.id] })]);

export const videoJobs = pgTable("video_jobs", {
  id: id(), brandId: brandId(), initiatedBy: varchar("initiated_by").notNull().references(() => users.id),
  workflow: text("workflow").$type<VideoWorkflow>().notNull(),
  blueprintId: varchar("blueprint_id").notNull(), versionId: varchar("version_id"),
  parentJobId: varchar("parent_job_id"),
  status: text("status").$type<"active" | "paused" | "complete" | "failed">().notNull().default("active"),
  currentStage: text("current_stage").$type<VideoStage>().notNull().default("created"),
  state: jsonb("state_json").$type<VideoJson>().notNull().default({}),
  lastAgentOutput: jsonb("last_agent_output_json").$type<VideoJson>(),
  error: jsonb("error_json").$type<VideoJson>(),
  autoRevisionCount: integer("auto_revision_count").notNull().default(0),
  maxAutoRevisions: integer("max_auto_revisions").notNull().default(2),
  attempt: integer("attempt").notNull().default(0),
  stageGeneration: integer("stage_generation").notNull().default(0),
  idempotencyKey: text("idempotency_key").notNull(),
  requestHash: text("request_hash").notNull(),
  stageStartedAt: timestamp("stage_started_at").notNull().defaultNow(),
  pausedAt: timestamp("paused_at"), pauseReason: text("pause_reason"),
  pausedTotalMs: numeric("paused_total_ms").notNull().default("0"),
  stagePausedMs: numeric("stage_paused_ms").notNull().default("0"),
  continuation: jsonb("continuation_json").$type<VideoContinuation>(),
  needsReviewReason: text("needs_review_reason"),
  createdAt: createdAt(), updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [
  unique("video_jobs_brand_identity").on(t.brandId, t.id),
  unique("video_jobs_version_identity").on(t.brandId, t.id, t.versionId),
  unique("video_jobs_creation_key").on(t.brandId, t.workflow, t.idempotencyKey),
  foreignKey({ columns: [t.brandId, t.blueprintId], foreignColumns: [videoBlueprints.brandId, videoBlueprints.id] }),
  foreignKey({ columns: [t.brandId, t.blueprintId, t.versionId], foreignColumns: [videoBlueprintVersions.brandId, videoBlueprintVersions.blueprintId, videoBlueprintVersions.id] }),
  foreignKey({ columns: [t.brandId, t.parentJobId], foreignColumns: [t.brandId, t.id] }),
  check("video_jobs_workflow_check", sql`${t.workflow} IN ('blueprint','production')`),
  check("video_jobs_production_version", sql`${t.workflow} <> 'production' OR ${t.versionId} IS NOT NULL`),
  check("video_jobs_revision_check", sql`${t.autoRevisionCount} >= 0 AND ${t.maxAutoRevisions} BETWEEN 0 AND 2 AND ${t.autoRevisionCount} <= ${t.maxAutoRevisions} AND ${t.attempt} >= 0`),
  check("video_jobs_status_check", sql`${t.status} IN ('active','paused','complete','failed')`),
  check("video_jobs_stage_attempt_check", sql`${t.stageGeneration} >= 0 AND ${t.attempt} BETWEEN 0 AND 2`),
  index("video_jobs_recovery_idx").on(t.status, t.updatedAt),
]);

export const videoVariants = pgTable("video_variants", {
  id: id(), brandId: brandId(), productionJobId: varchar("production_job_id").notNull(),
  blueprintId: varchar("blueprint_id").notNull(), versionId: varchar("version_id").notNull(),
  variantKey: text("variant_key").notNull(), variantPlan: jsonb("variant_plan_json").$type<VideoJson>().notNull(),
  projectId: text("project_id"), projectVersion: integer("project_version"), projectUri: text("tesseract_project_uri"),
  previewUri: text("preview_uri"), finalUri: text("final_uri"), status: text("status").notNull().default("planned"),
  qaResult: jsonb("qa_result_json").$type<VideoJson>(), createdAt: createdAt(),
}, t => [
  unique("video_variants_brand_identity").on(t.brandId, t.id),
  unique("video_variants_job_key").on(t.brandId, t.productionJobId, t.variantKey),
  foreignKey({ columns: [t.brandId, t.productionJobId, t.versionId], foreignColumns: [videoJobs.brandId, videoJobs.id, videoJobs.versionId] }),
  foreignKey({ columns: [t.brandId, t.blueprintId, t.versionId], foreignColumns: [videoBlueprintVersions.brandId, videoBlueprintVersions.blueprintId, videoBlueprintVersions.id] }),
]);

// Dispatch intents are append-only event identities with mutable delivery metadata.
// This keeps the foundation at nine tables without an in-memory outbox.
export const videoJobEvents = pgTable("video_job_events", {
  id: id(), brandId: brandId(), jobId: varchar("job_id").notNull(), stage: text("stage").$type<VideoStage>().notNull(),
  eventType: text("event_type").notNull(), payload: jsonb("payload_json").$type<VideoJson>().notNull(),
  operationKey: text("operation_key"), dispatchState: text("dispatch_state").$type<VideoDispatchState>(),
  admissionState: text("admission_state").$type<VideoAdmissionState>(),
  claimOwner: text("claim_owner"), claimToken: varchar("claim_token"), claimExpiresAt: timestamp("claim_expires_at"),
  providerTaskId: text("provider_task_id"), completionIdentity: text("completion_identity"),
  result: jsonb("result_json").$type<VideoJson>(), completedAt: timestamp("completed_at"), createdAt: createdAt(),
}, t => [
  unique("video_events_brand_identity").on(t.brandId, t.id),
  unique("video_events_operation_unique").on(t.brandId, t.jobId, t.operationKey),
  uniqueIndex("video_events_completion_unique").on(t.brandId, t.completionIdentity),
  foreignKey({ columns: [t.brandId, t.jobId], foreignColumns: [videoJobs.brandId, videoJobs.id] }),
  check("video_events_dispatch_check", sql`${t.dispatchState} IS NULL OR ${t.dispatchState} IN ('pending','claimed','completed')`),
  check("video_events_admission_check", sql`${t.admissionState} IS NULL OR ${t.admissionState} IN ('not_started','submitting','submitted','needs_review')`),
  index("video_events_claim_idx").on(t.dispatchState, t.claimExpiresAt),
]);

export const videoRenderArtifacts = pgTable("video_render_artifacts", {
  id: id(), brandId: brandId(), jobId: varchar("job_id").notNull(), versionId: varchar("version_id"),
  variantId: varchar("variant_id"), intentId: varchar("intent_id").notNull(),
  projectId: text("project_id").notNull(), projectVersion: integer("project_version").notNull(),
  kind: text("kind").$type<"preview" | "final" | "export" | "tsrct">().notNull(),
  uri: text("uri"), renderJobId: text("render_job_id").notNull().unique(),
  idempotencyKey: text("idempotency_key").notNull(), status: text("status").notNull().default("submitted"),
  error: jsonb("error_json").$type<VideoJson>(), completedAt: timestamp("completed_at"), createdAt: createdAt(),
}, t => [
  unique("video_artifacts_operation_unique").on(t.brandId, t.jobId, t.idempotencyKey),
  foreignKey({ columns: [t.brandId, t.jobId], foreignColumns: [videoJobs.brandId, videoJobs.id] }),
  foreignKey({ columns: [t.brandId, t.versionId], foreignColumns: [videoBlueprintVersions.brandId, videoBlueprintVersions.id] }),
  foreignKey({ columns: [t.brandId, t.variantId], foreignColumns: [videoVariants.brandId, videoVariants.id] }),
  foreignKey({ columns: [t.brandId, t.intentId], foreignColumns: [videoJobEvents.brandId, videoJobEvents.id] }),
]);

export const videoAssetRequests = pgTable("video_asset_requests", {
  id: id(), brandId: brandId(), jobId: varchar("job_id").notNull(), intentId: varchar("intent_id"),
  sceneId: text("scene_id"), slotKey: text("slot_key"), assetType: text("asset_type").$type<VideoAssetType>().notNull(),
  provider: text("provider").$type<"kie" | "fal">().notNull(), model: text("model").notNull(),
  requestHash: text("request_hash").notNull(), providerTaskId: text("provider_task_id"),
  admissionState: text("admission_state").$type<VideoAdmissionState>().notNull().default("not_started"),
  status: text("status").$type<"pending" | "generating" | "mirroring" | "ready" | "failed">().notNull().default("pending"),
  sourceUri: text("source_uri"), mirroredUri: text("mirrored_uri"),
  metadata: jsonb("metadata_json").$type<VideoAssetMetadata>(),
  costCredits: numeric("cost_credits"), attempt: integer("attempt").notNull().default(0),
  error: jsonb("error_json").$type<VideoJson>(), claimToken: varchar("claim_token"), claimExpiresAt: timestamp("claim_expires_at"),
  createdAt: createdAt(), updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, t => [
  unique("video_asset_requests_brand_identity").on(t.brandId, t.id),
  unique("video_asset_requests_job_hash").on(t.brandId, t.jobId, t.requestHash),
  foreignKey({ columns: [t.brandId, t.jobId], foreignColumns: [videoJobs.brandId, videoJobs.id] }),
  foreignKey({ columns: [t.brandId, t.intentId], foreignColumns: [videoJobEvents.brandId, videoJobEvents.id] }),
  check("video_asset_requests_ready_uri", sql`${t.status} <> 'ready' OR ${t.mirroredUri} IS NOT NULL`),
]);

export const videoAssets = pgTable("video_assets", {
  id: id(), brandId: brandId(), type: text("type").$type<VideoAssetType>().notNull(), uri: text("uri").notNull(),
  metadata: jsonb("metadata_json").$type<VideoAssetMetadata>().notNull(),
  origin: text("origin").$type<"library" | "generated" | "uploaded">().notNull(),
  requestHash: text("request_hash"), requestId: varchar("request_id"), createdAt: createdAt(),
}, t => [
  unique("video_assets_brand_identity").on(t.brandId, t.id),
  unique("video_assets_brand_hash").on(t.brandId, t.requestHash),
  foreignKey({ columns: [t.brandId, t.requestId], foreignColumns: [videoAssetRequests.brandId, videoAssetRequests.id] }),
]);

export type VideoJob = typeof videoJobs.$inferSelect;
export type VideoJobEvent = typeof videoJobEvents.$inferSelect;
export type VideoBlueprintVersion = typeof videoBlueprintVersions.$inferSelect;
